import * as React from 'react';
import {deps, inject, observer, StatefulComponent} from '../../lib/component';
import {ArticleImage} from '../../types/graphql';
import {ImageAspect, ImageSize, ImageSizeValue} from '../../constants';
import {percent, url} from 'csx';
import {classes, style} from 'typestyle';
import objectDifference from '../../lib/objectDifference';
import isObjectEqual from '../../lib/isObjectEqual';
import {centerCenter} from 'csstips';

type Props = {
  className?: string;
  innerClassName?: string;
  backgroundClassName?: any;
  exact?: string;
  image?: ArticleImage | null
  force?: ImageSize
  aspect?: ImageAspect
  wrapBackground?(src: string): string;
}

type State = {
  src: string;
  size: ImageSize;
  loading: boolean;
}

const defaultState = {
  src: '',
  size: null,
  loading: false,
};

@inject(deps) @observer
export default class InlinePreviewImage extends StatefulComponent<Props, State> {
  private _mounted: boolean;
  $el?: HTMLDivElement;

  state: State = {...defaultState};

  render() {
    const {children, exact, aspect, wrapBackground, image} = this.props;
    const {loading, size} = this.state;

    const paddingTop = aspect
      ? `${100 / aspect}%`
      : void 0;

    const src = this.getImageSource();

    const backgroundImage = src
      ? wrapBackground ? wrapBackground(url(src)) : url(src)
      : void 0;

    const className = classes(
      this.props.className,
      InlinePreviewImage.styles.container,
      !exact && loading && InlinePreviewImage.styles.containerLoading,
    );

    const styles = !aspect
      ? {backgroundImage}
      : {};

    const imageProps = {
      className: this.props.backgroundClassName,
      style: {backgroundImage},
      role: 'img',
      'aria-label': image.caption || void 0}
    ;

    return (
      <div className={className} style={styles} ref={el => this.$el = el} data-size={size}>
        {aspect && (
          <i style={{paddingTop}}/>
        )}
        {aspect && (
          <div {...imageProps}/>
        )}
        {aspect && children && (
          <main className={this.props.innerClassName}>{children}</main>
        )}
        {!aspect && children}
      </div>
    )
  }

  componentDidMount() {
    this._mounted = true;
    this.loadImage();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  componentDidUpdate(props) {
    if (!isObjectEqual(this.props.image, props.image)) {
      this.loadImage();
    }
  }

  protected getImageSource = () => {
    const {exact, force, image} = this.props;
    const {size, loading} = this.state;

    const isExact = !!exact;
    const isForced = !!force && !!image;
    const isLoading = loading && !!image;
    const isLoaded = !loading && !!size && !!image;

    switch (true) {
      case (isExact):
        return exact;

      case (isForced):
        return InlinePreviewImage.getBest(image, force);

      case (isLoading):
        return InlinePreviewImage.getWorst(image);

      case (isLoaded):
        return image[size];
    }
  };

  protected loadImage = () => {
    const {image} = this.props;
    if (!this.$el || !image)
      return this.setState({...defaultState});

    const {width = 64} = this.$el.getBoundingClientRect();
    const {devicePixelRatio = 1} = window;

    let current = width * devicePixelRatio;
    let size = InlinePreviewImage.nextGreatest(current);
    let match = image[size];

    while (!match && size) {
      size = InlinePreviewImage.nextWorst[size];
      match = image[size];
    }

    if (!match)
      return this.setState({...defaultState});

    this.setState({
      size: ImageSize.XSMALL,
      loading: true,
      src: InlinePreviewImage.getWorst(this.props.image)
    });

    const img = new Image();
    img.onload = () => {
      if (!this._mounted) return;
      this.setState({size, src: match, loading: false});
    };
    img.src = match;
  };

  static nextGreatest = (size: number) => {
    switch (true) {
      case (size > ImageSizeValue.large):
        return ImageSize.XLARGE;
      case (size > ImageSizeValue.medium):
        return ImageSize.LARGE;
      case (size > ImageSizeValue.small):
        return ImageSize.MEDIUM;
      case (size > ImageSizeValue.xsmall):
        return ImageSize.SMALL;
      default:
        return ImageSize.XSMALL;
    }
  };

  static nextWorst: {[key in ImageSize]: ImageSize | null} = {
    xlarge: ImageSize.LARGE,
    large: ImageSize.MEDIUM,
    medium: ImageSize.SMALL,
    small: ImageSize.XSMALL,
    xsmall: null,
  };

  static getBest = (available: ArticleImage | null | void, desired: ImageSize) => {
    if (!available)
      return '';

    return available[desired] || available.large || available.medium || available.main || '';
  };

  static getWorst = (available: ArticleImage | null | void) => {
    if (!available)
      return '';

    return available.xsmall || available.small || available.medium || available.main || '';
  };

  static styles = {
    container: style({
      width: percent(100),
      overflow: 'hidden',
      position: 'relative',
      backgroundPosition: 'center center',
      backgroundSize: 'cover',
      $nest: {
        '& > i': {
          zIndex: 1,
          display: 'block',
          width: percent(100),
        },
        '& > div': {
          position: 'absolute',
          zIndex: 0,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
          transition: `all 117ms ease-in-out`,
          willChange: 'transform, filter',
        },
        '& > main': {
          ...centerCenter,
          zIndex: 2,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }
      }
    }),
    containerLoading: style({
      $nest: {
        '& > div': {
          transform: `scale(1.5) transform3d(0, 1rem, 0)`,
          filter: `blur(1rem) grayscale(42%)`
        }
      }
    })
  }
}